/* ============================================
   NuevoPartner — Alta de partner por pasos (vista completa)
   Paso 1: Cuenta (elegir existente o crear nueva empresa/persona)
   Paso 2: Acceso (email + contraseña)
   Paso 3: Confirmar y crear
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { TipoEntidad, TipoDocumentoFiscal } from '@/lib/types'
import { PageHeader } from '@/components/ui'

interface ClienteLite {
  id: number
  empresa: string
  ruc_dni?: string | null
  tipo_entidad?: TipoEntidad | null
}

type Modo = 'existente' | 'nueva'

const STEPS = ['Cuenta', 'Acceso', 'Confirmar']

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let pw = ''
  for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)]
  return pw
}

export default function NuevoPartner() {
  const api = useApi()
  const navigate = useNavigate()

  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)

  // Paso 1 — cuenta
  const [modo, setModo] = useState<Modo>('existente')
  const [clientes, setClientes] = useState<ClienteLite[]>([])
  const [search, setSearch] = useState('')
  const [selectedClienteId, setSelectedClienteId] = useState<number | null>(null)
  // nueva cuenta
  const [nTipo, setNTipo] = useState<TipoEntidad>('empresa')
  const [nNombre, setNNombre] = useState('')
  const [nTipoDoc, setNTipoDoc] = useState<TipoDocumentoFiscal>('RUC')
  const [nDoc, setNDoc] = useState('')
  const [nRazonSocial, setNRazonSocial] = useState('')

  // Paso 2 — acceso
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState(generatePassword())

  const loadClientes = useCallback(async () => {
    try {
      const data = await api.get<ClienteLite[]>('/api/clientes')
      setClientes(Array.isArray(data) ? data : [])
    } catch {
      showToast('Error cargando empresas/personas', 'error')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadClientes() }, [loadClientes])

  // Al cambiar tipo de la nueva cuenta, ajustar documento por defecto
  function cambiarTipoNueva(t: TipoEntidad) {
    setNTipo(t)
    setNTipoDoc(t === 'persona' ? 'DNI' : 'RUC')
  }

  const filteredClientes = clientes.filter(c => {
    const q = search.toLowerCase()
    return c.empresa.toLowerCase().includes(q) || (c.ruc_dni || '').toLowerCase().includes(q)
  })

  const selectedCliente = clientes.find(c => c.id === selectedClienteId) || null

  // ── Validación por paso ──
  function validarPaso1(): boolean {
    if (modo === 'existente') {
      if (!selectedClienteId) { showToast('Selecciona una empresa o persona', 'error'); return false }
      return true
    }
    if (!nNombre.trim()) { showToast(nTipo === 'persona' ? 'El nombre completo es requerido' : 'El nombre de la empresa es requerido', 'error'); return false }
    if (nTipoDoc === 'RUC' && nDoc.trim() && !/^\d{11}$/.test(nDoc.trim())) { showToast('El RUC debe tener 11 dígitos', 'error'); return false }
    return true
  }

  function validarPaso2(): boolean {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { showToast('Ingresa un email válido', 'error'); return false }
    if (password.length < 6) { showToast('La contraseña debe tener al menos 6 caracteres', 'error'); return false }
    return true
  }

  function siguiente() {
    if (step === 0 && !validarPaso1()) return
    if (step === 1 && !validarPaso2()) return
    setStep(s => Math.min(s + 1, STEPS.length - 1))
  }
  function atras() { setStep(s => Math.max(s - 1, 0)) }

  async function crear() {
    if (saving) return
    setSaving(true)
    try {
      let clienteId = selectedClienteId

      // Crear la cuenta (empresa/persona) si es nueva
      if (modo === 'nueva') {
        const body: Record<string, any> = {
          empresa: nNombre.trim(),
          tipo_entidad: nTipo,
          tipo_documento_fiscal: nTipoDoc,
          ruc_dni: nDoc.trim() || null,
          razon_social: nRazonSocial.trim() || (nTipo === 'persona' ? nNombre.trim() : null),
          pais: 'PE',
          es_extranjero: false,
        }
        const created = await api.post<{ id: number }>('/api/clientes', body)
        clienteId = created?.id ?? null
        if (!clienteId) { showToast('No se pudo crear la cuenta', 'error'); setSaving(false); return }
      }

      const res = await api.post<{ user?: { id: number }; error?: string }>('/api/auth/register', {
        email: email.trim(), password, role: 'partner', cliente_id: clienteId,
      })
      if (res?.error) { showToast(res.error, 'error'); setSaving(false); return }

      showToast('Partner creado exitosamente', 'success')
      navigate('/clientes/partners')
    } catch (err: any) {
      showToast(err?.message || 'Error al crear el partner', 'error')
    } finally {
      setSaving(false)
    }
  }

  const nombreCuentaResumen = modo === 'existente'
    ? (selectedCliente?.empresa ?? '—')
    : (nNombre.trim() || '—')
  const tipoCuentaResumen = modo === 'existente'
    ? ((selectedCliente?.tipo_entidad ?? 'empresa') === 'persona' ? 'Persona' : 'Empresa')
    : (nTipo === 'persona' ? 'Persona' : 'Empresa')

  return (
    <div className="page-enter" style={{ maxWidth: 780, margin: '0 auto' }}>
      {/* Header */}
      <PageHeader breadcrumbs={[{ label: 'Partners', to: '/clientes/partners' }, { label: 'Nuevo' }]} />

      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
        {STEPS.map((label, i) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : '0 0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center',
                fontSize: '0.82rem', fontWeight: 700, flexShrink: 0,
                background: i <= step ? 'var(--primary-light)' : 'var(--bg-card-hover)',
                color: i <= step ? 'var(--text-on-primary)' : 'var(--text-muted)',
                border: i === step ? '2px solid var(--primary-light)' : '2px solid transparent',
              }}>{i < step ? '✓' : i + 1}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: i <= step ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: '0 12px', background: i < step ? 'var(--primary-light)' : 'var(--border)' }} />
            )}
          </div>
        ))}
      </div>

      {/* Contenido del paso */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1.5rem', background: 'var(--bg-card)', marginBottom: 20 }}>

        {/* PASO 1 — Cuenta */}
        {step === 0 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>¿A qué cuenta pertenece el partner?</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>Elige una empresa o persona ya registrada, o crea una nueva aquí mismo.</p>

            {/* Selector de modo */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              {([['existente', 'Elegir existente'], ['nueva', 'Crear nueva']] as const).map(([m, label]) => (
                <button key={m} type="button" onClick={() => setModo(m)} style={{
                  flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: modo === m ? '2px solid var(--primary-light)' : '1px solid var(--border)',
                  background: modo === m ? 'color-mix(in srgb, var(--primary-light) 10%, transparent)' : 'transparent',
                  color: 'var(--text-primary)', fontWeight: 600,
                }}>
                  {label}
                  <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 400, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {m === 'existente' ? 'Ya está en Clientes' : 'Empresa o persona nueva'}
                  </span>
                </button>
              ))}
            </div>

            {modo === 'existente' ? (
              <div>
                <input className="form-input" placeholder="Buscar por nombre o documento..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 10 }} />
                <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {filteredClientes.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: 8 }}>No hay resultados. Prueba "Crear nueva".</p>}
                  {filteredClientes.map(c => {
                    const isPersona = (c.tipo_entidad ?? 'empresa') === 'persona'
                    const active = selectedClienteId === c.id
                    return (
                      <button key={c.id} type="button" onClick={() => setSelectedClienteId(c.id)} style={{
                        display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '10px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                        border: active ? '2px solid var(--primary-light)' : '1px solid var(--border)',
                        background: active ? 'color-mix(in srgb, var(--primary-light) 10%, transparent)' : 'var(--bg-card-hover)', color: 'var(--text-primary)',
                      }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.empresa}</span>
                          {c.ruc_dni && <span style={{ display: 'block', fontSize: '0.76rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{c.ruc_dni}</span>}
                        </span>
                        <span style={{
                          fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: isPersona ? 'color-mix(in srgb, var(--warning) 15%, transparent)' : 'color-mix(in srgb, var(--primary-light) 15%, transparent)',
                          color: isPersona ? 'var(--accent)' : 'var(--primary-light)',
                        }}>{isPersona ? 'Persona' : 'Empresa'}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['empresa', 'persona'] as const).map(t => {
                    const active = nTipo === t
                    return (
                      <button key={t} type="button" onClick={() => cambiarTipoNueva(t)} style={{
                        flex: 1, padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                        border: active ? '2px solid var(--primary-light)' : '1px solid var(--border)',
                        background: active ? 'color-mix(in srgb, var(--primary-light) 10%, transparent)' : 'transparent',
                        color: active ? 'var(--primary-light)' : 'var(--text-secondary)',
                      }}>{t === 'empresa' ? 'Empresa' : 'Persona / Freelancer'}</button>
                    )
                  })}
                </div>
                <div className="form-group">
                  <label className="form-label">{nTipo === 'persona' ? 'Nombre completo *' : 'Nombre de la empresa *'}</label>
                  <input className="form-input" value={nNombre} onChange={e => setNNombre(e.target.value)} placeholder={nTipo === 'persona' ? 'Ej. Juan Pérez' : 'Ej. Agencia Digital SAC'} />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="form-group" style={{ flex: '0 0 140px' }}>
                    <label className="form-label">Tipo doc.</label>
                    <select className="form-select" value={nTipoDoc} onChange={e => setNTipoDoc(e.target.value as TipoDocumentoFiscal)}>
                      <option value="RUC">RUC</option><option value="DNI">DNI</option><option value="CE">CE</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">N° documento</label>
                    <input className="form-input" value={nDoc} onChange={e => setNDoc(e.target.value)} placeholder={nTipoDoc === 'RUC' ? '20XXXXXXXXX' : '7XXXXXXXX'} style={{ fontFamily: 'monospace' }} />
                  </div>
                </div>
                {nTipo === 'empresa' && (
                  <div className="form-group">
                    <label className="form-label">Razón social (opcional)</label>
                    <input className="form-input" value={nRazonSocial} onChange={e => setNRazonSocial(e.target.value)} placeholder="Nombre legal ante SUNAT" />
                  </div>
                )}
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>Podrás completar los datos fiscales y contactos luego en Clientes.</p>
              </div>
            )}
          </div>
        )}

        {/* PASO 2 — Acceso */}
        {step === 1 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>Datos de acceso del partner</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>Con estas credenciales el partner entrará a su panel. Se le pedirá cambiar la contraseña al iniciar sesión.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 460 }}>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="partner@agencia.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Contraseña inicial *</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-input" value={password} onChange={e => setPassword(e.target.value)} style={{ fontFamily: 'monospace' }} />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPassword(generatePassword())} title="Generar contraseña"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg></button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PASO 3 — Confirmar */}
        {step === 2 && (
          <div>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.05rem' }}>Revisa y confirma</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['Cuenta', `${nombreCuentaResumen}  ·  ${tipoCuentaResumen}`],
                ['Relación', 'Partner'],
                ['Email de acceso', email.trim() || '—'],
                ['Cuenta nueva', modo === 'nueva' ? 'Sí, se creará en Clientes' : 'No, existente'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 14px', background: 'var(--bg-card-hover)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{k}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navegación */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <button className="btn btn-secondary" onClick={step === 0 ? () => navigate('/clientes/partners') : atras} disabled={saving}>
          {step === 0 ? 'Cancelar' : 'Atrás'}
        </button>
        {step < STEPS.length - 1 ? (
          <button className="btn btn-primary" onClick={siguiente}>Siguiente</button>
        ) : (
          <button className="btn btn-primary" onClick={crear} disabled={saving}>{saving ? 'Creando...' : 'Crear partner'}</button>
        )}
      </div>
    </div>
  )
}
